//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';
import styled from 'styled-components';
import { useDebounce } from 'use-debounce';

import { InvisibleButton } from '@components/atoms/button/Button';
import { BodyBoldText, BodyText } from '@components/atoms/text/Text';
import { TextInput } from '@components/atoms/text-input/TextInput';
import VisuallyHiddenText from '@components/atoms/visually-hidden-text/VisuallyHiddenText';
import { Mixins } from '@theme/mixins';
import {
  SEARCH_DEBOUNCE_DELAY,
  UiaProductTrackingIds,
} from '@uia/products/constants/UiaProductConstants';
import { useRecentTrackingParamValuesQuery } from '@uia/products/hooks/useCurrentTrackingParamsQuery';

interface Props extends React.ComponentPropsWithoutRef<typeof TextInput> {
  label: string;
  value: string;
  paramName: string;
  isReadOnly: boolean;
  onChange: (value: string) => void;
}

export const TrackingParamInput = ({
  isReadOnly,
  label,
  onChange,
  paramName,
  value,
  ...textInputProps
}: Props) => {
  const [isFocused, setIsFocused] = useState(false);
  const [debouncedSearchTerm] = useDebounce(value, SEARCH_DEBOUNCE_DELAY);
  const { data: suggestions = [], isLoading } =
    useRecentTrackingParamValuesQuery(
      { paramName, searchTerm: debouncedSearchTerm },
      !isReadOnly && isFocused && !!value,
    );

  const handleSuggestionClick = (suggestionValue: string) => {
    onChange(suggestionValue);
    setIsFocused(false);
  };

  const shouldShowSuggestions = isFocused && !isReadOnly && !!value;

  return (
    <Wrapper>
      <TextInput
        {...textInputProps}
        aria-expanded={shouldShowSuggestions}
        isDisabled={isReadOnly}
        label={label}
        onBlur={() => setTimeout(() => setIsFocused(false), 100)} // Delay to allow click on suggestions
        onChange={onChange}
        onFocus={() => setIsFocused(true)}
        value={value}
      />
      {shouldShowSuggestions && (
        <SuggestionsContainer>
          {isLoading && (
            <NonSuggestion>
              <VisuallyHiddenText>
                Loading recently used parameters...
              </VisuallyHiddenText>
            </NonSuggestion>
          )}
          {!isLoading && suggestions.length === 0 && !!debouncedSearchTerm && (
            <NonSuggestion>
              <BodyText>No recently used parameters found.</BodyText>
            </NonSuggestion>
          )}
          {suggestions.map((suggestion) => (
            <SuggestionItem key={suggestion.value}>
              <Suggestion
                onClick={() => handleSuggestionClick(suggestion.value)}
                trackingId={
                  UiaProductTrackingIds.PRODUCT_EDITOR_TRACKING_PARAMS_EDITOR_RECENT_PARAM_SUGGESTION
                }
                type='button'
              >
                <BodyBoldText>{suggestion.value}</BodyBoldText>
              </Suggestion>
            </SuggestionItem>
          ))}
        </SuggestionsContainer>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const SuggestionsContainer = styled.ul(
  ({ theme }) => `
  ${Mixins.Sheet.Low}

  position: absolute;
  top: calc(100% + ${theme.spacings[1]});
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  max-height: calc(${theme.spacings[7]} * 2});
  padding: 0;
  overflow-y: auto;
  
  /* Position on top of any content below */
  z-index: ${theme.zindex.popover};
`,
);

const SuggestionItem = styled.li`
  width: 100%;
  list-style: none;
`;

const NonSuggestion = styled.div(
  ({ theme }) => `
  height: ${theme.spacings[6]};
  width: 100%;
  padding: ${theme.spacings[3]} ${theme.spacings[2]};
`,
);

const Suggestion = styled(InvisibleButton)(
  ({ theme }) => `
  height: ${theme.spacings[6]};
  width: 100%;
  
  & button {
    justify-content: flex-start;
    padding: ${theme.spacings[3]} ${theme.spacings[2]};
  }

  &:hover {
    background-color: ${theme.colors.gray0};
  }
`,
);
